import { collection, CollectionReference } from 'firebase/firestore';
import { db } from './firebase';
import type {
  CollectionCycle,
  WorkflowAssignment,
  DataSubmission,
  Comment,
  AuditLog,
  AppUser,
  UserPivotData
} from '@monthly-data-collection/shared';
import type { ArchivedDataSubmission } from '@monthly-data-collection/shared';
import type { HiringSpot } from '../shared/types/hiringSpot';
import type { ActivityEvent } from '../shared/types/activityEvent';

import { COLLECTION_NAMES } from '@monthly-data-collection/shared';
export { COLLECTION_NAMES };

/**
 * Typed collection references
 * These provide type safety when working with Firestore collections
 */
export const collections = {
  // User management collections
  appUsers: collection(db, COLLECTION_NAMES.APP_USERS) as CollectionReference<AppUser>,
  userPivotSync: collection(db, COLLECTION_NAMES.USER_PIVOT_SYNC) as CollectionReference<UserPivotData>,
  
  // Core workflow collections
  collectionCycles: collection(db, COLLECTION_NAMES.COLLECTION_CYCLES) as CollectionReference<CollectionCycle>,
  workflowAssignments: collection(db, COLLECTION_NAMES.WORKFLOW_ASSIGNMENTS) as CollectionReference<WorkflowAssignment>,
  dataSubmissions: collection(db, COLLECTION_NAMES.DATA_SUBMISSIONS) as CollectionReference<DataSubmission>,
  archivedDataSubmissions: collection(db, COLLECTION_NAMES.ARCHIVED_DATA_SUBMISSIONS) as CollectionReference<ArchivedDataSubmission>,
  
  // Collaboration collections
  comments: collection(db, COLLECTION_NAMES.COMMENTS) as CollectionReference<Comment>,
  
  // System collections
  auditLogs: collection(db, COLLECTION_NAMES.AUDIT_LOGS) as CollectionReference<AuditLog>,
  syncLogs: collection(db, COLLECTION_NAMES.SYNC_LOGS) as CollectionReference<any>,
  
  // BigQuery sync collections
  bigqueryCurrencies: collection(db, COLLECTION_NAMES.BIGQUERY_CURRENCIES) as CollectionReference<any>,
  bigqueryDeals: collection(db, COLLECTION_NAMES.BIGQUERY_DEALS) as CollectionReference<any>,
  bigquerySpend: collection(db, COLLECTION_NAMES.BIGQUERY_SPEND) as CollectionReference<any>,
  bigqueryWorkflows: collection(db, COLLECTION_NAMES.BIGQUERY_WORKFLOWS) as CollectionReference<any>,
  
  // Configuration collections (Phase 3)
  programConfigs: collection(db, COLLECTION_NAMES.PROGRAM_CONFIGS) as CollectionReference<any>,
  workflowConfigs: collection(db, COLLECTION_NAMES.WORKFLOW_CONFIGS) as CollectionReference<any>,
  workflows: collection(db, COLLECTION_NAMES.WORKFLOWS) as CollectionReference<any>,
  setupProgress: collection(db, COLLECTION_NAMES.SETUP_PROGRESS) as CollectionReference<any>,
  workflowDivisions: collection(db, COLLECTION_NAMES.WORKFLOW_DIVISIONS) as CollectionReference<any>,
  
  // Team Workflow collections (Three-collection design)
  workflowTeamPrograms: collection(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS) as CollectionReference<any>,
  workflowTeamTeams: collection(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS) as CollectionReference<any>,
  workflowTeamWorkflows: collection(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS) as CollectionReference<any>,

  // Workflow Spots collection (New workflow allocation system)
  workflowSpots: collection(db, COLLECTION_NAMES.WORKFLOW_SPOTS) as CollectionReference<any>,
  
  // Hiring Spots collection (Hiring capacity planning)
  hiringSpots: collection(db, COLLECTION_NAMES.HIRING_SPOTS) as CollectionReference<HiringSpot>,
  
  // Hiring Forecast collections (Hiring forecasting and dynamics)
  hiringForecasts: collection(db, COLLECTION_NAMES.HIRING_FORECASTS) as CollectionReference<any>,
  hiringDynamics: collection(db, COLLECTION_NAMES.HIRING_DYNAMICS) as CollectionReference<any>,
  hiringForecastsCalculated: collection(db, COLLECTION_NAMES.HIRING_FORECASTS_CALCULATED) as CollectionReference<any>,
  
  // Activity Feed collections (Workflow activity tracking)
  activityLog: collection(db, COLLECTION_NAMES.ACTIVITY_LOG) as CollectionReference<ActivityEvent>,
  
  // Integration configuration collections
  asanaConfig: collection(db, COLLECTION_NAMES.ASANA_CONFIG) as CollectionReference<any>,
  
  // Admin Google Sheets collection
  adminGoogleSheets: collection(db, COLLECTION_NAMES.ADMIN_GOOGLE_SHEETS) as CollectionReference<any>,

  // Goals domain collections
  goalsRunCards: collection(db, COLLECTION_NAMES.GOALS_RUN_CARDS) as CollectionReference<any>,
  goalsRunSettings: collection(db, COLLECTION_NAMES.GOALS_RUN_SETTINGS) as CollectionReference<any>,
  goalsTransformInitiatives: collection(db, COLLECTION_NAMES.GOALS_TRANSFORM_INITIATIVES) as CollectionReference<any>,
  goalsGrowPortfolios: collection(db, COLLECTION_NAMES.GOALS_GROW_PORTFOLIOS) as CollectionReference<any>,
};

/**
 * Document ID generators for consistent ID patterns
 */
class DocumentIdGenerators {
  /**
   * Generate collection cycle ID (YYYY-MM format)
   */
  static collectionCycleId(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  /**
   * Generate workflow assignment ID
   */
  static workflowAssignmentId(cycleId: string, workflowId: string): string {
    return `${cycleId}-${workflowId}`;
  }

  /**
   * Generate archived data submission ID
   * Now uses workflowId only for single-snapshot architecture
   */
  static archivedDataSubmissionId(workflowId: string): string {
    return workflowId;
  }

  /**
   * Generate data submission ID
   */
  static dataSubmissionId(assignmentId: string, version: number = 1): string {
    return `${assignmentId}-v${version}`;
  }

  /**
   * Generate comment ID
   */
  static commentId(submissionId: string, timestamp: Date = new Date()): string {
    return `${submissionId}-${timestamp.getTime()}`;
  }

  /**
   * Generate audit log ID
   */
  static auditLogId(timestamp: Date = new Date()): string {
    return `audit-${timestamp.getTime()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export { DocumentIdGenerators };